import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { useOriginalUnitTypeSelect, useVisibleUnitTypes } from './useCatalogsStore';
import { colorThemes } from '../../settings';
import {
    IComponentName,
    IDateRange,
    IFilterBoolean,
    IFilterSelect,
    IFilterSelectComposed,
    IGetApiType,
    ILanguageId,
    IParamsList,
    ISelect,
    IUiIdSpinner,
} from '../../interfaces';
import { UnitTypeValues } from '../../middleware';
import { getApiTypeHeader, getApiTypeWithUnitType } from '../../instruments';

// ------------ Spinner ------------
export function useUiSpinnerCount(): number {
    return useContext(MobXProviderContext).RootStore.uiStore.spinnerCount;
}

export function useUiIsLoading(): boolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isSpinning;
}

export function useUiSpinnerList(): IUiIdSpinner[] {
    return useContext(MobXProviderContext).RootStore.uiStore.spinners;
}
export function useUiIsSpinnerFound(ids: IGetApiType[] = []): boolean {
    // ToDo: 24.05.2021 - NOT USE for big components
    const spinnersIds = useUiSpinnerList();
    let isLoading = false;
    spinnersIds.forEach((spinner: IUiIdSpinner) => {
        ids.forEach((id) => {
            if (spinner.includes(getApiTypeHeader(id))) {
                isLoading = true;
            }
        });
    });

    return isLoading;
}

export function useUiIsSpinnerTypeUnit(apiTypeId: IGetApiType, unitTypeId: IFilterSelectComposed): boolean {
    const spinnersIds = useUiSpinnerList();
    const spinnerId = getApiTypeWithUnitType(apiTypeId, unitTypeId);

    return spinnersIds.includes(spinnerId);
}

// ------------ Page List ------------
export const useIsLocalFilter = (): boolean => {
    return useContext(MobXProviderContext).RootStore.uiStore.isLocalFilter;
};
export function useUiTotal(): number {
    return useContext(MobXProviderContext).RootStore.uiStore.total;
}
export function useUiPageNumber(): number {
    return useContext(MobXProviderContext).RootStore.uiStore.pageNumber;
}
export function useUiPageSize(): number {
    return useContext(MobXProviderContext).RootStore.uiStore.pageSize;
}

export function useUiDateRange(): IDateRange {
    return useContext(MobXProviderContext).RootStore.uiStore.dateRange;
}
export function useUiSearch(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.search;
}
export function useUiCompanyId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.companyId;
}
export function useUiSellerId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.sellerId;
}
export function useUiBuyerId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.buyerId;
}
export function useUiCompanyTypeId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.companyTypeId;
}
export function useUiPaymentType(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.paymentType;
}
export function useUiPackageId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.packageId;
}
export function useUiOperatorId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.operatorId;
}
export function useUiCardSetId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.cardSetId;
}
export function useUiSessionId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.sessionId;
}
export function useUiZoneId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.zoneId;
}
export function useUiUserId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.userId;
}
export function useUiCardId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.cardId;
}
export function useUiTariffId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.tariffId;
}
export function useUiTariffPackageId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.tariffPackageId;
}
export function useUiOperatorInteractionsUploadFileStatusId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.uploadFileStatusId;
}
export function useUiOperatorInteractionsUploadFileId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.fileId;
}
export function useUiOperatorInteractionsGatewayId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.gatewayId;
}
export function useUiOperatorInteractionsCommandId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.commandId;
}
export function useUiEntityId(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.entityId;
}

export function useUiUnitTypeValue(): UnitTypeValues | undefined {
    const values = useContext(MobXProviderContext).RootStore.uiStore.unitTypes;
    if (!values || values.length === 0) {
        return undefined;
    } else if (typeof values === 'string') {
        return values as UnitTypeValues;
    } else if (Array.isArray(values)) {
        return values[0] as UnitTypeValues;
    }
}
/**
 * The unit type a page should actually render: the selected one when the tenant may see it,
 * otherwise the first visible one. Undefined only when the tenant may see none.
 */
export function useUiEffectiveUnitType(): UnitTypeValues | undefined {
    const selectedUnitType = useUiUnitTypeValue();
    const visibleUnitTypes = useVisibleUnitTypes();

    return selectedUnitType && visibleUnitTypes.includes(selectedUnitType) ? selectedUnitType : visibleUnitTypes[0];
}
export function useUiUnitTypeValues(): IFilterSelectComposed {
    const values = useContext(MobXProviderContext).RootStore.uiStore.unitTypes;
    if (!values || (Array.isArray(values) && values.length === 0)) {
        return undefined;
    } else {
        return values;
    }
}

export function useUiSource(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.source;
}
export function useUiIsDelete(): IFilterBoolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isDelete;
}
export function useUiWithDeleted(): IFilterBoolean {
    return useContext(MobXProviderContext).RootStore.uiStore.withDeleted;
}
export function useUiIsActiveThisMonth(): IFilterBoolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isActiveThisMonth;
}
export function useUiIsEimProvided(): IFilterBoolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isEimProvided;
}
export function useUiLastViewComponentName(): IComponentName {
    return useContext(MobXProviderContext).RootStore.uiStore.lastViewComponentName;
}
export function useUiStatus(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.status;
}
export function useUiStatusesList(): Array<ISelect> {
    return useContext(MobXProviderContext).RootStore.uiStore.statusesList;
}

export function useUiImportType(): IFilterSelect {
    return useContext(MobXProviderContext).RootStore.uiStore.importType;
}

export function useUiViewInfo(): IParamsList {
    const {
        buyerId,
        cardSetId,
        commandId,
        companyId,
        companyTypeId,
        date,
        dateRange: { startDate, endDate },
        entityId,
        fileId,
        gatewayId,
        importType,
        isDelete,
        operatorId,
        pageNumber,
        pageSize,
        paymentType,
        routeName,
        search,
        sellerId,
        sessionId,
        status,
        unitTypes,
        userId,
        withDeleted,
        zoneId,
    } = useContext(MobXProviderContext).RootStore.uiStore;

    return {
        buyerId,
        cardSetId,
        commandId,
        companyId,
        companyTypeId,
        status,
        date,
        day: date ? date.day() : undefined,
        month: date ? date.month() + 1 : undefined,
        year: date ? date.year() : undefined,
        endDate,
        entityId,
        fileId,
        gatewayId,
        importType,
        isDelete,
        operatorId,
        pageNumber,
        pageSize,
        paymentType,
        routeName,
        search,
        sellerId,
        sessionId,
        startDate,
        unitTypes,
        userId,
        withDeleted,
        zoneId,
    };
}

export function useUiDarkTheme(): boolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isDarkTheme;
}

export function useUiLanguage(): ILanguageId {
    return useContext(MobXProviderContext).RootStore.uiStore.languageId;
}

export function useUiChangedTheme(): boolean {
    return useContext(MobXProviderContext).RootStore.uiStore.isChangedTheme;
}

export function useUiInitPageList() {
    return useContext(MobXProviderContext).RootStore.uiStore.initPageList;
}

export function useUiCurrentUnitType(): ISelect | undefined {
    const currentUnitTypeId = useContext(MobXProviderContext).RootStore.uiStore.unitTypeIds;
    const unitTypesSelect = useOriginalUnitTypeSelect();

    return unitTypesSelect?.find((i: ISelect) => i.id === currentUnitTypeId);
}

type IAxis = {
    format?: string;
    gridlines?: { color: string; count?: number };
    maxValue?: number;
    minValue?: number;
    textStyle?: { color: string; fontSize?: number };
    ticks?: number[];
    title?: string;
    viewWindow?: {
        max?: number;
        min?: number;
    };
    // [otherOptionKey: string]: any;
};

type IUiChartsSettingsAnnotations = {
    alwaysOutside?: boolean;
    highContrast?: boolean;
    textStyle?: {
        auraColor?: string;
        fontName?: string;
        fontSize?: number;
        bold?: boolean;
        italic?: boolean;
        color?: string;
        opacity?: number;
    };
};
export type IUiChartsSettings = {
    annotationsInside?: IUiChartsSettingsAnnotations;
    annotationsOutside?: IUiChartsSettingsAnnotations;
    backgroundColor: string;
    chartBackground: string;
    enableInteractivity?: boolean;
    hAxis?: IAxis;
    vAxis?: IAxis;
    textColor: string;
    animation?: {
        duration: number;
        easing: 'out';
        startup: boolean;
    };
};

export function useChartsSettings(): IUiChartsSettings {
    const isDark = useContext(MobXProviderContext).RootStore.uiStore.isDarkTheme;

    const backgroundColor = isDark
        ? colorThemes.dark['--main-background-color']
        : colorThemes.light['--main-background-color'];
    const textColor = isDark ? colorThemes.dark['--main-text-color'] : colorThemes.light['--main-text-color'];

    return {
        backgroundColor,
        chartBackground: 'transparent',
        textColor,
        annotationsOutside: {
            alwaysOutside: true,
            highContrast: true,
            textStyle: {
                auraColor: backgroundColor,
            },
        },
        annotationsInside: {
            alwaysOutside: false,
            highContrast: true,
            textStyle: {
                auraColor: backgroundColor,
                color: textColor,
            },
        },
        hAxis: {
            textStyle: { color: textColor, fontSize: 10 },
            gridlines: {
                color: textColor,
            },
        },
        vAxis: {
            format: 'short',
            textStyle: { color: textColor, fontSize: 10 },
            gridlines: {
                color: textColor,
            },
        },
        animation: {
            duration: 700,
            easing: 'out',
            startup: true,
        },
    };
}
